import { v4 as uuid } from "uuid";
import { z } from "zod";
import { EmployeeSocialFactor, EmploymentLevel, Gender, Occupation, OccupationType } from "../../entities/contractorEmployee";
import { SocialSpendCertificate, SocialSpendEntityType } from "../../entities/socialSpend";
import { isABNInAllowlist } from "../../utils/abn";
import { getRandomColour } from "../../utils/color";
import { isValidDistance } from "../../utils/location";
import { resolveCriteriaSets } from "../../utils/targetCriteriaSets";
import { isEmployeeCostSourced } from "../../utils/trackingTargets";
import { SocialCommitmentTypeSpend } from "../packageSocialSpendCommitment";
import { ProjectSocialSpendCommitmentType } from "./enums";
import { ProjectCommmitmentGenerationSource } from "../project/enums";
import { getLocationBasedSocialSpendDefaults } from "../project/projectLocationDefaults";
import { LabourHourFactorCountingMethod } from "../projectLabourHourCommitment/enums";
/**
 * One CRITERIA SET of a social spend Target Definition: the filtering half, and
 * nothing else. See {@link LabourHourCommitmentCriteriaSet} for why criteria sets exist
 * and why the target stays on the commitment.
 *
 * Carries the UNION of the two filter shapes a spend Target Definition can
 * take, because the shape is chosen by the parent's `targetMetricType` and the
 * two are mutually exclusive:
 *
 * - invoice-sourced -> the SUPPLIER filters: `entityTypes`, `certifications`,
 *   post codes, `abns`, `maxDistance`. These are what {@link getSocialSpendFactors}
 *   evaluates against a Social Spend row.
 * - employee-cost-sourced -> the EMPLOYEE filters, the same set a labour hours
 *   criteria set carries. An employee-cost definition is skipped by the invoice
 *   matcher entirely, so these are read on the employee-cost path instead.
 *
 * The parent enforces "no supplier filters on an employee-cost target" with a
 * DB CHECK. A Postgres CHECK cannot read another table, so a criteria set cannot
 * restate it; the write path does — see `assertSpendCriteriaSetShape`.
 */
export const socialSpendCommitmentCriteriaSetSchema = z.object({
    abns: z.array(z.string()).optional(),
    certifications: z.array(z.enum(SocialSpendCertificate)).optional(),
    employmentLevel: z.array(z.enum(EmploymentLevel)).optional(),
    entityTypes: z.array(z.enum(SocialSpendEntityType)).optional(),
    excludedPostCodes: z.array(z.string()).optional(),
    factorCountingMethod: z.enum(LabourHourFactorCountingMethod).optional(),
    gender: z.array(z.enum(Gender)).optional(),
    id: z.string(),
    maxDistance: z.number().optional(),
    newEntrantToConstruction: z.boolean().optional(),
    occupation: z.array(z.enum(Occupation)).optional(),
    occupationType: z.array(z.enum(OccupationType)).optional(),
    /** Tab order in the authoring form. 0-based, unique within a commitment. */
    ordinal: z.number(),
    postCodes: z.array(z.string()).optional(),
    socialFactors: z.array(z.enum(EmployeeSocialFactor)).optional()
});
export const projectSocialSpendCommitmentLineItemSchema = z.object({
    abns: z.array(z.string()).optional(),
    certifications: z.array(z.enum(SocialSpendCertificate)).optional(),
    colour: z.string(),
    created: z.date(),
    /**
     * The criteria sets that qualify a row for this target, ORed together.
     *
     * Optional and possibly EMPTY, which means "fall back to the flat filter
     * fields on this object". Every commitment gets one criteria set by backfill, so an
     * empty array in practice means a row written by an older build — see
     * `resolveCriteriaSets`, the one place that resolves the fallback.
     */
    criteriaSets: z.array(socialSpendCommitmentCriteriaSetSchema).optional(),
    deleted: z.boolean().optional(),
    employmentLevel: z.array(z.enum(EmploymentLevel)).optional(),
    entityTypes: z.array(z.enum(SocialSpendEntityType)).optional(),
    excludedPostCodes: z.array(z.string()).optional(),
    /**
     * How the configured filters combine. Optional and unset today, so behaviour
     * is unchanged; the backing column arrives with the metric type in #2991.
     */
    factorCountingMethod: z.enum(LabourHourFactorCountingMethod).optional(),
    gender: z.array(z.enum(Gender)).optional(),
    generationSource: z.enum(ProjectCommmitmentGenerationSource).optional(),
    id: z.string(),
    maxDistance: z.number().optional(),
    newEntrantToConstruction: z.boolean().optional(),
    occupation: z.array(z.enum(Occupation)).optional(),
    occupationType: z.array(z.enum(OccupationType)).optional(),
    postCodes: z.array(z.string()).optional(),
    projectId: z.string(),
    socialFactors: z.array(z.enum(EmployeeSocialFactor)).optional(),
    sourceFrameworkId: z.string().optional(),
    sourceFrameworkTargetId: z.string().optional(),
    targetDescription: z.string().optional(),
    targetMetricType: z.enum(ProjectSocialSpendCommitmentType),
    targetName: z.enum(SocialCommitmentTypeSpend),
    targetValue: z.number(),
    targetValueRealised: z.number()
});
export const createDefaultSocialSpendCommitmentsForProject = (projectId, country, state) => {
    // Get location-specific defaults, or null if not found
    const locationDefaults = getLocationBasedSocialSpendDefaults(country, state);
    // If no location defaults found, return empty array
    if (!locationDefaults) {
        return [];
    }
    // Create commitments from location-specific defaults
    return Object.values(SocialCommitmentTypeSpend).reduce((acc, sc) => {
        if (sc !== SocialCommitmentTypeSpend.DefaultSpend &&
            sc !== SocialCommitmentTypeSpend.GeespApprenticeProgram) {
            const commitmentConfiguration = locationDefaults[sc];
            if (commitmentConfiguration) {
                return [
                    ...acc,
                    {
                        ...commitmentConfiguration,
                        achievedValue: 0,
                        colour: getRandomColour(),
                        created: new Date(),
                        generationSource: ProjectCommmitmentGenerationSource.Custom,
                        id: uuid(),
                        projectId,
                        targetMetricType: ProjectSocialSpendCommitmentType.Invoice,
                        targetName: sc,
                        targetValueRealised: 0
                    }
                ];
            }
        }
        return [...acc];
    }, []);
};
export const createDefaultSocialSpendCommitment = (projectId) => {
    return {
        achievedValue: 0,
        colour: "#000000",
        created: new Date(),
        generationSource: ProjectCommmitmentGenerationSource.Custom,
        id: uuid(),
        projectId,
        projectSocialSpendCommitmentId: uuid(),
        targetMetricType: ProjectSocialSpendCommitmentType.Invoice,
        targetName: SocialCommitmentTypeSpend.DefaultSpend,
        targetValue: 0,
        targetValueRealised: 0
    };
};
export const isValidEntityType = (socialSpendEntity, entities) => {
    if (entities && entities.length > 0) {
        // A generated row has no entity type, so it cannot satisfy a configured
        // entity-type filter.
        return (socialSpendEntity !== undefined && entities.includes(socialSpendEntity));
    }
    return true;
};
export const isValidCertifcation = (socialSpendCertificates, certifications) => {
    if (certifications && certifications.length > 0) {
        return (socialSpendCertificates.filter((x) => certifications.includes(x)).length >
            0);
    }
    return true;
};
export const isValidIncludedPostCode = (postCodeToCheck, postCodes) => {
    if (postCodes && postCodes.length > 0) {
        if (!postCodeToCheck) {
            return false;
        }
        return postCodes.includes(postCodeToCheck);
    }
    return true;
};
export const isValidExcludedPostCode = (socialSpendPostCode, postCodes) => {
    if (postCodes && postCodes.length > 0) {
        if (!socialSpendPostCode) {
            return false;
        }
        return !postCodes.includes(socialSpendPostCode);
    }
    return true;
};
// Default debug configuration
const DEFAULT_DEBUG_CONFIG = {
    enabled: false,
    logLevel: "basic"
};
// Individual validation functions with debug support
const validateEntityType = (commitment, socialSpend, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isValidEntityType(socialSpend.entityType, commitment.entityTypes);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`Entity type validation: ${isValid ? "PASS" : "FAIL"}`, {
            requiredEntityTypes: commitment.entityTypes,
            socialSpendEntityType: socialSpend.entityType
        });
    }
    return {
        details: {
            requiredEntityTypes: commitment.entityTypes,
            socialSpendEntityType: socialSpend.entityType
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend entity type does not match required entity types"
    };
};
const validateCertification = (commitment, socialSpend, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isValidCertifcation(socialSpend.certifications, commitment.certifications);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`Certification validation: ${isValid ? "PASS" : "FAIL"}`, {
            requiredCertifications: commitment.certifications,
            socialSpendCertifications: socialSpend.certifications
        });
    }
    return {
        details: {
            requiredCertifications: commitment.certifications,
            socialSpendCertifications: socialSpend.certifications
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend certifications do not match required certifications"
    };
};
const validateIncludedPostCode = (commitment, socialSpend, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isValidIncludedPostCode(socialSpend.supplierPostCode, commitment.postCodes);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`Included post code validation: ${isValid ? "PASS" : "FAIL"}`, {
            requiredPostCodes: commitment.postCodes,
            socialSpendPostCode: socialSpend.supplierPostCode
        });
    }
    return {
        details: {
            requiredPostCodes: commitment.postCodes,
            socialSpendPostCode: socialSpend.supplierPostCode
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend post code not in included post codes"
    };
};
const validateExcludedPostCode = (commitment, socialSpend, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isValidExcludedPostCode(socialSpend.supplierPostCode, commitment.excludedPostCodes);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`Excluded post code validation: ${isValid ? "PASS" : "FAIL"}`, {
            excludedPostCodes: commitment.excludedPostCodes,
            socialSpendPostCode: socialSpend.supplierPostCode
        });
    }
    return {
        details: {
            excludedPostCodes: commitment.excludedPostCodes,
            socialSpendPostCode: socialSpend.supplierPostCode
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend post code is in excluded post codes"
    };
};
const validateABN = (commitment, socialSpend, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isABNInAllowlist(socialSpend.abn, commitment.abns);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`ABN validation: ${isValid ? "PASS" : "FAIL"}`, {
            requiredABNs: commitment.abns,
            socialSpendABN: socialSpend.abn
        });
    }
    return {
        details: {
            requiredABNs: commitment.abns,
            socialSpendABN: socialSpend.abn
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend ABN does not match required ABNs"
    };
};
const validateDistance = (commitment, socialSpend, project, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const isValid = isValidDistance(commitment.maxDistance, socialSpend.geoLocationLat, socialSpend.geoLocationLong, project.projectAddress.geoLocationLat, project.projectAddress.geoLocationLong);
    if (debugConfig.enabled && debugConfig.logLevel !== "none") {
        console.log(`Distance validation: ${isValid ? "PASS" : "FAIL"}`, {
            maxDistance: commitment.maxDistance,
            projectLocation: {
                lat: project.projectAddress.geoLocationLat,
                lng: project.projectAddress.geoLocationLong
            },
            socialSpendLocation: {
                lat: socialSpend.geoLocationLat,
                lng: socialSpend.geoLocationLong
            }
        });
    }
    return {
        details: {
            maxDistance: commitment.maxDistance,
            projectLocation: {
                lat: project.projectAddress.geoLocationLat,
                lng: project.projectAddress.geoLocationLong
            },
            socialSpendLocation: {
                lat: socialSpend.geoLocationLat,
                lng: socialSpend.geoLocationLong
            }
        },
        isValid,
        reason: isValid
            ? undefined
            : "Social spend location exceeds maximum distance from project"
    };
};
/**
 * Keep only the results of filters this Target Definition actually configures,
 * mirroring the Labour Hours matcher. Every predicate below self-passes when its
 * field is unset, so under {@link LabourHourFactorCountingMethod.AllFactorsCombined}
 * this is equivalent to running all six — the pre-filter changes nothing.
 *
 * It is what makes {@link LabourHourFactorCountingMethod.AnyMatchingFactor}
 * meaningful: under `.some()` an unconfigured, self-passing predicate would
 * match every Social Spend row.
 */
const getConfiguredValidationResults = (commitment, validationResults) => {
    const configured = [];
    if (commitment.maxDistance && Number(commitment.maxDistance) > 0) {
        configured.push(validationResults[0]);
    }
    if (commitment.entityTypes && commitment.entityTypes.length > 0) {
        configured.push(validationResults[1]);
    }
    if (commitment.certifications && commitment.certifications.length > 0) {
        configured.push(validationResults[2]);
    }
    if (commitment.postCodes && commitment.postCodes.length > 0) {
        configured.push(validationResults[3]);
    }
    if (commitment.excludedPostCodes && commitment.excludedPostCodes.length > 0) {
        configured.push(validationResults[4]);
    }
    if (commitment.abns && commitment.abns.length > 0) {
        configured.push(validationResults[5]);
    }
    return configured;
};
/**
 * Evaluate ONE filter set — one criteria set, or a pre-criteria-set commitment's own flat
 * fields. {@link validateCommitment} is what callers want; this is its per-set
 * half, and it must not resolve criteria sets itself or it would recurse.
 */
const validateFilters = (commitment, socialSpend, project, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const validationResults = [
        validateDistance(commitment, socialSpend, project, debugConfig),
        validateEntityType(commitment, socialSpend, debugConfig),
        validateCertification(commitment, socialSpend, debugConfig),
        validateIncludedPostCode(commitment, socialSpend, debugConfig),
        validateExcludedPostCode(commitment, socialSpend, debugConfig),
        validateABN(commitment, socialSpend, debugConfig)
    ];
    const configuredValidationResults = getConfiguredValidationResults(commitment, validationResults);
    const factorCountingMethod = commitment.factorCountingMethod ??
        LabourHourFactorCountingMethod.AllFactorsCombined;
    const isValid = configuredValidationResults.length === 0
        ? true
        : factorCountingMethod ===
            LabourHourFactorCountingMethod.AnyMatchingFactor
            ? configuredValidationResults.some((result) => result.isValid)
            : configuredValidationResults.every((result) => result.isValid);
    if (debugConfig.enabled && debugConfig.logLevel === "detailed") {
        console.log(`Commitment validation summary:`, {
            commitmentId: commitment.id,
            configuredValidationSteps: configuredValidationResults.map((result) => validationResults.indexOf(result) + 1),
            factorCountingMethod,
            isValid,
            validationResults: validationResults.map((result, index) => ({
                isValid: result.isValid,
                reason: result.reason,
                step: index + 1
            }))
        });
    }
    return { isValid, validationResults };
};
/**
 * Does this Target Definition count this Social Spend row?
 *
 * Matches when ANY criteria set matches, each criteria set's own filters combining by its
 * own `factorCountingMethod`. One criteria set — or a pre-criteria-set commitment, via
 * {@link resolveCriteriaSets} — is the old behaviour exactly.
 *
 * As on the labour hours side, the OR lives here rather than in
 * {@link getSocialSpendFactors} so that every caller gets it, including
 * {@link validateSingleCommitment}. `validationResults` is the winning criteria set's
 * on a match and every criteria set's failures flattened on a miss.
 */
const validateCommitment = (commitment, socialSpend, project, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    const perCriteriaSet = resolveCriteriaSets(commitment).map((criteriaSet) => validateFilters(criteriaSet, socialSpend, project, debugConfig));
    const matched = perCriteriaSet.find((result) => result.isValid);
    if (matched) {
        return matched;
    }
    return {
        isValid: false,
        validationResults: perCriteriaSet.flatMap((result) => result.validationResults)
    };
};
// Filter commitments that are available in the contractor package
const getAvailableCommitments = (project, contractorPackage) => {
    return project.commitmentsSpend.filter((ch) => Boolean(contractorPackage?.commitmentsSpend.find((pch) => pch.projectSocialSpendCommitmentId == ch.id)));
};
export const getSocialSpendFactors = (socialSpend, project, contractorPackage, debugConfig = DEFAULT_DEBUG_CONFIG) => {
    if (!project) {
        if (debugConfig.enabled) {
            console.log("No project provided, returning empty array");
        }
        return [];
    }
    if (debugConfig.enabled) {
        console.log("Starting social spend factors validation:", {
            debugLevel: debugConfig.logLevel,
            projectId: project.id,
            socialSpendId: socialSpend.id
        });
    }
    // Get available commitments from the contractor package
    const availableCommitments = getAvailableCommitments(project, contractorPackage);
    if (debugConfig.enabled) {
        console.log(`Found ${availableCommitments.length} available commitments`);
    }
    // Keep invoices off generated-only Target Definitions. This generalises a
    // hardcoded skip of the legacy GEESP target type: its real purpose was that a
    // Target Definition fed by generated employee cost must not also absorb
    // invoices. Without it, an EmployeeCost definition configuring no supplier
    // filters would match *every* invoice on the Project, since unconfigured
    // filters do not participate.
    const filteredCommitments = availableCommitments.filter((c) => !isEmployeeCostSourced(c.targetMetricType));
    if (debugConfig.enabled) {
        console.log(`After excluding employee-cost-only targets: ${filteredCommitments.length} commitments`);
    }
    // Filter commitments based on validation criteria
    const validCommitments = filteredCommitments.filter((commitment) => {
        const validation = validateCommitment(commitment, socialSpend, project, debugConfig);
        if (debugConfig.enabled && !validation.isValid) {
            console.log(`Commitment ${commitment.id} failed validation:`, validation.validationResults
                .filter((result) => !result.isValid)
                .map((result) => result.reason));
        }
        return validation.isValid;
    });
    if (debugConfig.enabled) {
        console.log(`Social spend factors validation complete: ${validCommitments.length} valid commitments found`);
    }
    return validCommitments;
};
// Utility functions for debugging and testing
export const createDebugConfig = (enabled = true, logLevel = "basic") => ({
    enabled,
    logLevel
});
export const getSocialSpendFactorsWithDebug = (socialSpend, project, contractorPackage, debugEnabled = true, logLevel = "detailed") => {
    return getSocialSpendFactors(socialSpend, project, contractorPackage, createDebugConfig(debugEnabled, logLevel));
};
// Function to get detailed validation results for a single commitment
export const validateSingleCommitment = (commitment, socialSpend, project, debugConfig = createDebugConfig(true, "detailed")) => {
    return validateCommitment(commitment, socialSpend, project, debugConfig);
};
// Function to get all validation results for debugging purposes
export const getDetailedValidationResults = (socialSpend, project, contractorPackage) => {
    if (!project) {
        return { allResults: [], validCommitments: [] };
    }
    const availableCommitments = getAvailableCommitments(project, contractorPackage);
    const filteredCommitments = availableCommitments.filter((c) => !isEmployeeCostSourced(c.targetMetricType));
    const allResults = filteredCommitments.map((commitment) => ({
        commitment,
        validation: validateCommitment(commitment, socialSpend, project, createDebugConfig(false))
    }));
    const validCommitments = allResults
        .filter((result) => result.validation.isValid)
        .map((result) => result.commitment);
    return { allResults, validCommitments };
};
